import React, { useState, useEffect } from 'react';
import { 
  Store, Check, X, Clock, Ban, Users, Package, 
  TrendingUp, Mail, Phone, MapPin, FileText, Percent,
  Eye, Trash2, AlertCircle, UserMinus, UserPlus, RotateCcw
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import AdminPageHeader from '../../components/AdminPageHeader';
import axiosInstance from '../../api/axios';
import { toast } from 'sonner';

const VendorsManager = () => {
  const [vendors, setVendors] = useState([]);
  const [demotedVendors, setDemotedVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [activeTab, setActiveTab] = useState('pending');
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [showCommissionDialog, setShowCommissionDialog] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [commissionRate, setCommissionRate] = useState(0);
  
  useEffect(() => {
    loadVendors();
    loadDemotedVendors();
  }, []);

  const loadVendors = async () => {
    try {
      const response = await axiosInstance.get('/vendors/admin/all');
      setVendors(response.data);
    } catch (error) {
      console.error('Error loading vendors:', error);
      toast.error('Fehler beim Laden der Verkäufer');
    } finally {
      setLoading(false);
    }
  };

  const loadDemotedVendors = async () => {
    try {
      const response = await axiosInstance.get('/vendors/admin/demoted');
      setDemotedVendors(response.data);
    } catch (error) {
      console.error('Error loading demoted vendors:', error);
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      pending: { label: 'Ausstehend', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
      approved: { label: 'Freigegeben', color: 'bg-green-100 text-green-800', icon: Check },
      rejected: { label: 'Abgelehnt', color: 'bg-red-100 text-red-800', icon: X },
      suspended: { label: 'Gesperrt', color: 'bg-red-100 text-red-800', icon: Ban },
      demoted: { label: 'Herabgestuft', color: 'bg-orange-100 text-orange-800', icon: UserMinus }
    };
    const c = config[status] || config.pending;
    const Icon = c.icon;
    return (
      <Badge className={`${c.color} flex items-center gap-1`}>
        <Icon className="h-3 w-3" />
        {c.label}
      </Badge>
    );
  };

  const handleApprove = async (vendorId) => {
    try {
      await axiosInstance.post(`/vendors/admin/${vendorId}/approve?commission_rate=${commissionRate}`);
      toast.success('Verkäufer wurde freigegeben');
      loadVendors();
      setSelectedVendor(null);
      setShowCommissionDialog(false);
    } catch (error) {
      toast.error('Fehler beim Freigeben');
    }
  };

  const handleReject = async () => {
    if (!selectedVendor) return;
    try {
      await axiosInstance.post(`/vendors/admin/${selectedVendor.id}/reject?reason=${encodeURIComponent(rejectReason)}`);
      toast.success('Verkäufer wurde abgelehnt');
      loadVendors();
      setShowRejectDialog(false);
      setSelectedVendor(null);
      setRejectReason('');
    } catch (error) {
      toast.error('Fehler beim Ablehnen');
    }
  };

  const handleSuspend = async (vendorId) => {
    if (!confirm('Möchten Sie diesen Verkäufer wirklich sperren? Alle seine Produkte werden deaktiviert.')) return;
    try {
      await axiosInstance.post(`/vendors/admin/${vendorId}/suspend`);
      toast.success('Verkäufer wurde gesperrt');
      loadVendors();
    } catch (error) {
      toast.error('Fehler beim Sperren');
    }
  };

  const handleUnsuspend = async (vendorId) => {
    if (!confirm('Möchten Sie diesen Verkäufer wieder freigeben?')) return;
    try {
      await axiosInstance.post(`/vendors/admin/${vendorId}/unsuspend`);
      toast.success('Verkäufer wurde wieder freigeschaltet');
      loadVendors();
    } catch (error) {
      toast.error('Fehler beim Freischalten');
    }
  };

  const handleDelete = async (vendorId) => {
    if (!confirm('Möchten Sie diesen Verkäufer und alle seine Produkte wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.')) return;
    try {
      await axiosInstance.delete(`/vendors/admin/${vendorId}`);
      toast.success('Verkäufer wurde gelöscht');
      loadVendors();
      setSelectedVendor(null);
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  const handleDemoteToCustomer = async (vendorId) => {
    if (!confirm(
      'Verkäufer zum Kunden herabstufen?\n\n' +
      '• Account bleibt erhalten\n' +
      '• Produkte werden deaktiviert (nicht gelöscht)\n' +
      '• Kann später wiederhergestellt werden'
    )) return;
    
    try {
      const response = await axiosInstance.post(
        `/vendors/admin/${vendorId}/demote-to-customer`,
        null,
        { params: { reason: 'Administrativ herabgestuft' } }
      );
      toast.success(response.data.message);
      loadVendors();
      loadDemotedVendors();
      setSelectedVendor(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Herabstufen');
    }
  };

  const handleRestoreVendor = async (userId) => {
    if (!confirm('Möchten Sie diesen Benutzer wieder als Verkäufer aktivieren?')) return;
    
    try {
      const response = await axiosInstance.post(`/vendors/admin/${userId}/restore-vendor-status`);
      toast.success(response.data.message);
      loadVendors();
      loadDemotedVendors();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Wiederherstellen');
    }
  };

  const handleUpdateCommission = async () => {
    if (!selectedVendor) return;
    try {
      await axiosInstance.put(`/vendors/admin/${selectedVendor.id}/commission?commission_rate=${commissionRate}`);
      toast.success('Provision aktualisiert');
      loadVendors();
      setShowCommissionDialog(false);
    } catch (error) {
      toast.error('Fehler beim Aktualisieren');
    }
  };

  const filteredVendors = vendors.filter(v => {
    const status = v.vendor_profile?.status;
    if (activeTab === 'all') return true;
    return status === activeTab;
  });

  const pendingCount = vendors.filter(v => v.vendor_profile?.status === 'pending').length;
  const approvedCount = vendors.filter(v => v.vendor_profile?.status === 'approved').length;

  if (loading) {
    return <div className="p-8">Lade Verkäufer...</div>;
  }

  return (
    <div className="p-4 sm:p-8 max-w-7xl mx-auto" style={{ backgroundColor: '#F5EDE4', minHeight: '100vh' }}>
      <AdminPageHeader 
        title="Verkäuferverwaltung"
        subtitle="Verwalten Sie alle Verkäufer auf Ihrem Marktplatz"
        icon={Store}
      />

      {/* Section: Statistics - Goldbraune Überschrift */}
      <div className="mb-3 p-2 px-3 flex items-center gap-2" style={{ backgroundColor: '#C4A084' }}>
        <div className="w-6 h-6 rounded flex items-center justify-center bg-[#F0FFF0]">
          <TrendingUp className="h-4 w-4 text-[#60B060]" />
        </div>
        <h2 className="text-base font-semibold text-white">Statistiken</h2>
      </div>

      {/* Stats - Compact cards with very light pastel colors */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
        <Card className="rounded-none border-[#C4A084]" style={{ backgroundColor: '#F0F8FF' }}>
          <CardContent className="p-2 text-center">
            <div className="w-8 h-8 mx-auto mb-1 rounded flex items-center justify-center bg-white">
              <Users className="h-4 w-4 text-[#60A0D0]" />
            </div>
            <div className="text-lg font-bold text-[#5D4E3C]">{vendors.length}</div>
            <div className="text-xs text-[#8B7355]">Gesamt</div>
          </CardContent>
        </Card>
        <Card className={`rounded-none border-[#C4A084] ${pendingCount > 0 ? 'ring-1 ring-[#C4A084]' : ''}`} style={{ backgroundColor: '#FFF8F0' }}>
          <CardContent className="p-2 text-center">
            <div className="w-8 h-8 mx-auto mb-1 rounded flex items-center justify-center bg-white">
              <Clock className="h-4 w-4 text-[#E89860]" />
            </div>
            <div className="text-lg font-bold text-[#5D4E3C]">{pendingCount}</div>
            <div className="text-xs text-[#8B7355]">Warten auf Freigabe</div>
          </CardContent>
        </Card>
        <Card className="rounded-none border-[#C4A084]" style={{ backgroundColor: '#F0FFF0' }}>
          <CardContent className="p-2 text-center">
            <div className="w-8 h-8 mx-auto mb-1 rounded flex items-center justify-center bg-white">
              <Check className="h-4 w-4 text-[#60B060]" />
            </div>
            <div className="text-lg font-bold text-[#5D4E3C]">{approvedCount}</div>
            <div className="text-xs text-[#8B7355]">Aktive Verkäufer</div>
          </CardContent>
        </Card>
        <Card className="rounded-none border-[#C4A084]" style={{ backgroundColor: '#F8F0FF' }}>
          <CardContent className="p-2 text-center">
            <div className="w-8 h-8 mx-auto mb-1 rounded flex items-center justify-center bg-white">
              <Package className="h-4 w-4 text-[#A070D0]" />
            </div>
            <div className="text-lg font-bold text-[#5D4E3C]">
              {vendors.reduce((sum, v) => sum + (v.product_count || 0), 0)}
            </div>
            <div className="text-xs text-[#8B7355]">Verkäufer-Produkte</div>
          </CardContent>
        </Card>
      </div>

      {/* Info Banner */}
      {pendingCount > 0 && (
        <Card className="mb-4 border-[#C4A084] rounded-none" style={{ backgroundColor: '#FFF8F0' }}>
          <CardContent className="p-3">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-[#E89860]" />
              <span className="text-[#5D4E3C] text-sm">
                <strong>{pendingCount}</strong> Verkäufer warten auf Ihre Freigabe
              </span>
              <Button 
                size="sm" 
                variant="outline"
                className="ml-auto text-xs h-7 border-[#C4A084] text-[#C4A084]"
                onClick={() => setActiveTab('pending')}
              >
                Jetzt prüfen
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Section: Vendor List - Goldbraune Überschrift */}
      <div className="mb-3 p-2 px-3 flex items-center gap-2" style={{ backgroundColor: '#C4A084' }}>
        <div className="w-6 h-6 rounded flex items-center justify-center bg-[#F0F8FF]">
          <Store className="h-4 w-4 text-[#60A0D0]" />
        </div>
        <h2 className="text-base font-semibold text-white">Verkäuferliste</h2>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Vendors List */}
        <div className="lg:col-span-2">
          <Card className="border-[#C4A084]">
            <CardHeader className="pb-2 p-0">
              <div className="p-3" style={{ backgroundColor: '#C4A084' }}>
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                  <TabsList className="flex flex-wrap h-auto gap-1 bg-transparent p-0">
                    <TabsTrigger 
                      value="pending" 
                      className="text-xs px-2 py-1.5 bg-[#FFF8F0] text-[#E89860] data-[state=active]:bg-white data-[state=active]:text-[#5D4E3C] rounded-none"
                    >
                      Ausstehend {pendingCount > 0 && `(${pendingCount})`}
                    </TabsTrigger>
                    <TabsTrigger 
                      value="approved" 
                      className="text-xs px-2 py-1.5 bg-[#F0FFF0] text-[#60B060] data-[state=active]:bg-white data-[state=active]:text-[#5D4E3C] rounded-none"
                    >
                      Aktiv
                    </TabsTrigger>
                    <TabsTrigger 
                      value="rejected" 
                      className="text-xs px-2 py-1.5 bg-[#FFF0F0] text-[#D06060] data-[state=active]:bg-white data-[state=active]:text-[#5D4E3C] rounded-none"
                    >
                      Abgelehnt
                    </TabsTrigger>
                    <TabsTrigger 
                      value="demoted" 
                      className="text-xs px-2 py-1.5 bg-[#F8F0FF] text-[#A070D0] data-[state=active]:bg-white data-[state=active]:text-[#5D4E3C] rounded-none"
                    >
                      Herabg. {demotedVendors.length > 0 && `(${demotedVendors.length})`}
                    </TabsTrigger>
                    <TabsTrigger 
                      value="all" 
                      className="text-xs px-2 py-1.5 bg-[#F0F8FF] text-[#60A0D0] data-[state=active]:bg-white data-[state=active]:text-[#5D4E3C] rounded-none"
                    >
                      Alle
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>
            </CardHeader>
            <CardContent>
              {/* Demoted Vendors Tab */}
              {activeTab === 'demoted' ? (
                demotedVendors.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <UserMinus className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>Keine herabgestuften Verkäufer</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {demotedVendors.map((user) => (
                      <div key={user.id} className="p-4 border rounded-lg bg-orange-50 border-orange-200">
                        <div className="flex justify-between items-start">
                          <div>
                            <h3 className="font-semibold flex items-center gap-2">
                              <UserMinus className="h-4 w-4 text-orange-600" />
                              {user.vendor_profile?.shop_name || 'Unbekannt'}
                            </h3>
                            <p className="text-sm text-gray-600">
                              {user.first_name} {user.last_name} • {user.email}
                            </p>
                            <p className="text-xs text-orange-600 mt-1">
                              Grund: {user.vendor_profile?.demotion_reason || 'Nicht angegeben'}
                            </p>
                            <p className="text-xs text-gray-500">
                              {user.product_count || 0} Produkte (deaktiviert)
                            </p>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-green-600 border-green-300"
                            onClick={() => handleRestoreVendor(user.id)}
                          >
                            <RotateCcw className="h-4 w-4 mr-1" />
                            Wiederherstellen
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )
              ) : filteredVendors.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Store className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>Keine Verkäufer in dieser Kategorie</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredVendors.map((vendor) => (
                    <div
                      key={vendor.id}
                      onClick={() => setSelectedVendor(vendor)}
                      className={`p-4 border rounded-lg cursor-pointer transition-all hover:shadow-md ${
                        selectedVendor?.id === vendor.id ? 'border-blue-500 bg-blue-50' : ''
                      }`}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="font-semibold flex items-center gap-2">
                            <Store className="h-4 w-4" />
                            {vendor.vendor_profile?.shop_name || 'Unbenannt'}
                          </h3>
                          <p className="text-sm text-gray-600">
                            {vendor.first_name} {vendor.last_name}
                          </p>
                          <p className="text-xs text-gray-400">{vendor.email}</p>
                        </div>
                        <div className="text-right">
                          {getStatusBadge(vendor.vendor_profile?.status)}
                          <p className="text-xs text-gray-500 mt-1">
                            {vendor.product_count || 0} Produkte
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Vendor Details */}
        <div>
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Verkäufer-Details</CardTitle>
            </CardHeader>
            <CardContent>
              {selectedVendor ? (
                <div className="space-y-4">
                  <div className="text-center pb-4 border-b">
                    <div className="w-16 h-16 bg-gray-200 rounded-full mx-auto mb-3 flex items-center justify-center">
                      <Store className="h-8 w-8 text-gray-500" />
                    </div>
                    <h3 className="font-bold text-lg">
                      {selectedVendor.vendor_profile?.shop_name}
                    </h3>
                    {getStatusBadge(selectedVendor.vendor_profile?.status)}
                  </div>

                  <div className="space-y-3 text-sm">
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4 text-gray-400" />
                      <span>{selectedVendor.first_name} {selectedVendor.last_name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-gray-400" />
                      <span>{selectedVendor.email}</span>
                    </div>
                    {selectedVendor.phone && (
                      <div className="flex items-center gap-2">
                        <Phone className="h-4 w-4 text-gray-400" />
                        <span>{selectedVendor.phone}</span>
                      </div>
                    )}
                    {selectedVendor.vendor_profile?.business_address && (
                      <div className="flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-gray-400" />
                        <span>{selectedVendor.vendor_profile.business_address}</span>
                      </div>
                    )}
                    {selectedVendor.vendor_profile?.tax_id && (
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-gray-400" />
                        <span>St.-Nr.: {selectedVendor.vendor_profile.tax_id}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <Percent className="h-4 w-4 text-gray-400" />
                      <span>Provision: {selectedVendor.vendor_profile?.commission_rate || 0}%</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Package className="h-4 w-4 text-gray-400" />
                      <span>{selectedVendor.product_count || 0} Produkte</span>
                    </div>
                  </div>

                  {selectedVendor.vendor_profile?.shop_description && (
                    <div className="pt-3 border-t">
                      <Label className="text-xs text-gray-500">Shop-Beschreibung</Label>
                      <p className="text-sm mt-1">{selectedVendor.vendor_profile.shop_description}</p>
                    </div>
                  )}

                  {selectedVendor.vendor_profile?.rejection_reason && (
                    <div className="pt-3 border-t">
                      <Label className="text-xs text-red-500">Ablehnungsgrund</Label>
                      <p className="text-sm mt-1 text-red-600">{selectedVendor.vendor_profile.rejection_reason}</p>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="pt-4 border-t space-y-2">
                    {selectedVendor.vendor_profile?.status === 'pending' && (
                      <>
                        <Button 
                          className="w-full" 
                          onClick={() => {
                            setCommissionRate(0);
                            setShowCommissionDialog(true);
                          }}
                        >
                          <Check className="h-4 w-4 mr-2" />
                          Freigeben
                        </Button>
                        <Button 
                          variant="destructive" 
                          className="w-full"
                          onClick={() => setShowRejectDialog(true)}
                        >
                          <X className="h-4 w-4 mr-2" />
                          Ablehnen
                        </Button>
                      </>
                    )}
                    
                    {selectedVendor.vendor_profile?.status === 'approved' && (
                      <>
                        <Button 
                          variant="outline" 
                          className="w-full border-[#C4B5A4] hover:bg-[#E8E0D5] text-gray-700"
                          onClick={() => {
                            setCommissionRate(selectedVendor.vendor_profile?.commission_rate || 0);
                            setShowCommissionDialog(true);
                          }}
                        >
                          <Percent className="h-4 w-4 mr-2" />
                          Provision ändern
                        </Button>
                        <Button 
                          variant="outline" 
                          className="w-full border-[#C4B5A4] hover:bg-amber-50 text-gray-700"
                          onClick={() => handleDemoteToCustomer(selectedVendor.id)}
                        >
                          <UserMinus className="h-4 w-4 mr-2" />
                          Zum Kunden herabstufen
                        </Button>
                        <Button 
                          variant="outline" 
                          className="w-full border-[#C4B5A4] hover:bg-red-50 text-gray-700"
                          onClick={() => handleSuspend(selectedVendor.id)}
                        >
                          <Ban className="h-4 w-4 mr-2" />
                          Sperren
                        </Button>
                      </>
                    )}

                    {selectedVendor.vendor_profile?.status === 'rejected' && (
                      <Button 
                        className="w-full" 
                        onClick={() => {
                          setCommissionRate(0);
                          setShowCommissionDialog(true);
                        }}
                      >
                        <Check className="h-4 w-4 mr-2" />
                        Doch freigeben
                      </Button>
                    )}

                    {selectedVendor.vendor_profile?.status === 'suspended' && (
                      <Button 
                        className="w-full bg-green-600 hover:bg-green-700" 
                        onClick={() => handleUnsuspend(selectedVendor.id)}
                      >
                        <Check className="h-4 w-4 mr-2" />
                        Entsperren & Freigeben
                      </Button>
                    )}

                    <Button 
                      variant="ghost" 
                      className="w-full text-red-600"
                      onClick={() => handleDelete(selectedVendor.id)}
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Löschen
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Eye className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>Wählen Sie einen Verkäufer aus</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Reject Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Verkäufer ablehnen</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Label>Ablehnungsgrund (optional)</Label>
            <Textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Grund für die Ablehnung eingeben..."
              className="mt-2"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRejectDialog(false)} className="rounded-none border-[#D4C4B0]">
              Abbrechen
            </Button>
            <Button variant="destructive" onClick={handleReject} className="rounded-none">
              Ablehnen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Commission Dialog */}
      <Dialog open={showCommissionDialog} onOpenChange={setShowCommissionDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {selectedVendor?.vendor_profile?.status === 'pending' 
                ? 'Verkäufer freigeben' 
                : 'Provision ändern'}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Label>Provision (%)</Label>
            <p className="text-sm text-gray-500 mb-2">
              Anteil, den Sie von jedem Verkauf dieses Verkäufers erhalten (0 = keine Provision)
            </p>
            <Input
              type="number"
              min="0"
              max="100"
              step="0.5"
              value={commissionRate}
              onChange={(e) => setCommissionRate(parseFloat(e.target.value) || 0)}
              className="mt-2"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCommissionDialog(false)} className="rounded-none border-[#D4C4B0]">
              Abbrechen
            </Button>
            <Button onClick={() => {
              if (selectedVendor?.vendor_profile?.status === 'pending') {
                handleApprove(selectedVendor.id);
              } else {
                handleUpdateCommission();
              }
            }} className="rounded-none bg-[#C4A484] hover:bg-[#B89968]">
              {selectedVendor?.vendor_profile?.status === 'pending' ? 'Freigeben' : 'Speichern'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VendorsManager;
